import { useEffect, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import homeHero from '../../assets/images/home-hero.png'
import PrimaryButton from '../common/PrimaryButton'
import SecondaryButton from '../common/SecondaryButton'
import { PageContainer } from '../common/PageContainer'
import { DimensionCard } from './DimensionCard'
import HeroSection from './HeroSection'
import IndiaMapDimensions from './IndiaMapDimensions'
import { dimensions, DESIGN_WIDTH, DESIGN_HEIGHT, STUDENT_WELFARE_CARD_INDEX } from '../../data/homeDimensions'
import { scale } from '../../theme/scale'
import { useHomeScrollAnimation } from '../../hooks/useHomeScrollAnimation'

const SCROLL_DISTANCE_VH = 280

export default function HomeScrollExperience() {
  const trackRef = useRef(null)
  const stageRef = useRef(null)
  const stageScaleRef = useRef(1)
  const [stageScale, setStageScale] = useState(1)
  const [reducedMotion, setReducedMotion] = useState(false)

  const studentWelfareHero = dimensions[STUDENT_WELFARE_CARD_INDEX].image

  const layerRefs = useRef({
    stage: null,
    darkOverlay: null,
    heroImage: null,
    heroImgHome: null,
    heroImgMorph: null,
    heroGradient: null,
    heroText: null,
    dimensionsStage: null,
    title: null,
    cardWrappers: [],
    cardRotators: [],
    cardImages: [],
    cardImageContainers: [],
    heroImageClip: null,
  })

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const updateReducedMotion = () => setReducedMotion(mediaQuery.matches)
    updateReducedMotion()
    mediaQuery.addEventListener('change', updateReducedMotion)
    return () => mediaQuery.removeEventListener('change', updateReducedMotion)
  }, [])

  useEffect(() => {
    const updateScale = () => {
      if (!stageRef.current) return
      const width = stageRef.current.offsetWidth
      const nextScale = Math.min(width / DESIGN_WIDTH, 1)
      stageScaleRef.current = nextScale
      setStageScale(nextScale)
    }

    updateScale()
    const observer = new ResizeObserver(updateScale)
    if (stageRef.current) observer.observe(stageRef.current)
    return () => observer.disconnect()
  }, [])

  useHomeScrollAnimation(trackRef, layerRefs, !reducedMotion, stageScaleRef)

  if (reducedMotion) {
    return (
      <>
        <HeroSection />
        <IndiaMapDimensions />
      </>
    )
  }

  return (
    <Box
      ref={trackRef}
      component="section"
      sx={{
        height: `calc(100vh + ${SCROLL_DISTANCE_VH}vh)`,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflow: 'hidden',
          bgcolor: '#111',
          contain: 'layout style paint',
        }}
      >
        <Box
          ref={(node) => {
            stageRef.current = node
            layerRefs.current.stage = node
          }}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            maxWidth: DESIGN_WIDTH,
            mx: 'auto',
            overflow: 'hidden',
          }}
        >
          <Box
            ref={(node) => {
              layerRefs.current.darkOverlay = node
            }}
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: '#111',
              opacity: 0,
              pointerEvents: 'none',
              willChange: 'opacity',
            }}
          />

          <Box
            ref={(node) => {
              layerRefs.current.heroImage = node
            }}
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 1,
              transform: 'translate3d(0, 0, 0) scale(1) rotate(0deg)',
              transformOrigin: 'center center',
              overflow: 'visible',
              zIndex: 2,
              pointerEvents: 'none',
              willChange: 'transform, opacity, background-color',
              backfaceVisibility: 'hidden',
            }}
          >
            <Box
              ref={(node) => {
                layerRefs.current.heroImageClip = node
              }}
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '24px',
                overflow: 'hidden',
                willChange: 'border-radius',
              }}
            >
              <Box
                ref={(node) => {
                  layerRefs.current.heroGradient = node
                }}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 1,
                  background: 'linear-gradient(65deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 96%)',
                  willChange: 'opacity',
                  pointerEvents: 'none',
                }}
              />
              <Box
                ref={(node) => {
                  layerRefs.current.heroImgHome = node
                }}
                component="img"
                src={homeHero}
                alt=""
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: 'translateZ(0)',
                  opacity: 1,
                  willChange: 'opacity',
                }}
              />
              <Box
                ref={(node) => {
                  layerRefs.current.heroImgMorph = node
                }}
                component="img"
                src={studentWelfareHero}
                alt=""
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: 'translateZ(0)',
                  opacity: 0,
                  willChange: 'opacity',
                }}
              />
            </Box>
          </Box>

          <Box
            ref={(node) => {
              layerRefs.current.heroText = node
            }}
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 3,
              display: 'flex',
              alignItems: 'center',
              opacity: 1,
              transform: 'translate3d(0, 0, 0)',
              willChange: 'transform, opacity',
              backfaceVisibility: 'hidden',
            }}
          >
            <PageContainer sx={{ py: 0 }}>
              <Box sx={{ maxWidth: 660 }}>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: 36, sm: 48, md: 64 },
                    color: '#fffefa',
                    lineHeight: 1.1,
                    mb: 3,
                  }}
                >
                  Empowering Youth Through Leadership & Nation Building
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 16, md: 20 },
                    color: 'rgba(255,254,250,0.8)',
                    lineHeight: 1.5,
                    mb: 4,
                  }}
                >
                  Disha Foundation is dedicated to developing young leaders who will shape India's
                  future through leadership development programs, cultural initiatives, women empowerment,
                  and good governance awareness.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                  <PrimaryButton to="/plc-4" showArrow={false}>Explore PLC 4.0</PrimaryButton>
                  <SecondaryButton to="/events">Disha Sessions</SecondaryButton>
                </Box>
              </Box>
            </PageContainer>
          </Box>

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 4,
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: DESIGN_WIDTH,
                height: DESIGN_HEIGHT * stageScale,
                overflow: 'hidden',
              }}
            >
              <Box
                ref={(node) => {
                  layerRefs.current.dimensionsStage = node
                }}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0,
                  pointerEvents: 'none',
                  willChange: 'opacity',
                }}
              >
                <Box
                  sx={{
                    width: DESIGN_WIDTH,
                    height: DESIGN_HEIGHT,
                    transform: `scale(${stageScale})`,
                    transformOrigin: 'top left',
                    position: 'relative',
                  }}
                >
                  <Box
                    ref={(node) => {
                      layerRefs.current.title = node
                    }}
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: 'calc(50% - 70px)',
                      transform: 'translateX(-50%)',
                      textAlign: 'center',
                      color: '#fffefa',
                      zIndex: 1,
                      pointerEvents: 'none',
                      width: '100%',
                      px: 2,
                      opacity: 0,
                      willChange: 'opacity',
                    }}
                  >
                    <Typography
                      component="h2"
                      sx={{
                        fontSize: scale.fontSize.sectionTitle,
                        lineHeight: 1.1,
                        fontWeight: 400,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {`The foundation works \nacross\u00a07 key dimensions\u00a0`}
                    </Typography>
                  </Box>

                  {dimensions.map((dim, index) => {
                    const card = (
                      <DimensionCard
                        title={dim.title}
                        description={dim.description}
                        image={dim.image}
                        icon={dim.icon}
                        iconBgColor={dim.iconBgColor}
                        path={dim.path}
                        imageHeight={dim.imageHeight}
                        imageScale={dim.imageScale}
                        educationLayout={dim.educationLayout}
                        imageRef={
                          index === STUDENT_WELFARE_CARD_INDEX
                            ? (node) => {
                                layerRefs.current.cardImages[index] = node
                              }
                            : undefined
                        }
                        imageContainerRef={
                          index === STUDENT_WELFARE_CARD_INDEX
                            ? (node) => {
                                layerRefs.current.cardImageContainers[index] = node
                              }
                            : undefined
                        }
                      />
                    )

                    if (dim.rotateOrigin) {
                      return (
                        <Box
                          key={dim.title}
                          ref={(node) => {
                            layerRefs.current.cardWrappers[index] = node
                          }}
                          sx={{
                            position: 'absolute',
                            left: dim.left,
                            top: dim.top,
                            zIndex: dim.zIndex,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transform: 'translate3d(0, 0, 0) scale(0.82)',
                            willChange: 'transform, opacity',
                            backfaceVisibility: 'hidden',
                          }}
                        >
                          <Box
                            ref={(node) => {
                              layerRefs.current.cardRotators[index] = node
                            }}
                            sx={{
                              transform: `rotate(${dim.rotate}deg)`,
                              willChange: 'transform',
                              backfaceVisibility: 'hidden',
                            }}
                          >
                            {card}
                          </Box>
                        </Box>
                      )
                    }

                    return (
                      <Box
                        key={dim.title}
                        ref={(node) => {
                          layerRefs.current.cardWrappers[index] = node
                          layerRefs.current.cardRotators[index] = null
                        }}
                        sx={{
                          position: 'absolute',
                          left: dim.left,
                          top: dim.top,
                          zIndex: dim.zIndex,
                          opacity: 0,
                          transform: `translate3d(0, 0, 0) scale(0.82) rotate(${dim.rotate}deg)`,
                          willChange: 'transform, opacity',
                          backfaceVisibility: 'hidden',
                        }}
                      >
                        {card}
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
